import React, { useEffect, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DiffResultView } from "@/components/common/DiffResultView";
import { TimelineDiffPanel } from "@/components/workspace/TimelineDiffPanel";
import { TID } from "@/lib/testIds";

export default function Compare() {
    const [workspaces, setWorkspaces] = useState([]);
    const [a, setA] = useState("");
    const [b, setB] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/workspaces");
                setWorkspaces(data);
                if (data.length >= 2) {
                    setA(data[1].id); setB(data[0].id);
                } else if (data.length === 1) {
                    setA(data[0].id);
                }
            } catch (e) { console.warn(e); }
        })();
    }, []);

    const run = async () => {
        if (!a || !b) { toast.error("Select both workspaces"); return; }
        if (a === b) { toast.error("Pick two different workspaces"); return; }
        setLoading(true); setResult(null);
        try {
            const { data } = await api.get("/workspaces/compare", { params: { a, b } });
            setResult(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Compare failed");
        } finally { setLoading(false); }
    };

    return (
        <div className="space-y-6">
            <Tabs defaultValue="cross">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger data-testid={TID.compareTab("cross")} value="cross">Cross-Workspace Diff</TabsTrigger>
                    <TabsTrigger data-testid={TID.compareTab("timeline")} value="timeline">Timeline Diff</TabsTrigger>
                </TabsList>

                <TabsContent value="cross" className="space-y-6 pt-4">
                    <Panel
                        eyebrow="Workspace Diff"
                        title="Compare two engineering workspaces"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
                            <div>
                                <div className="label-eyebrow mb-1.5">Baseline · A</div>
                                <Select value={a} onValueChange={setA}>
                                    <SelectTrigger data-testid="compare-a-select" className="bg-[var(--bg-surface-2)] border-white/10 text-xs h-9">
                                        <SelectValue placeholder="Choose workspace A" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                                        {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="text-cyan-400 px-2 hidden md:block"><ArrowLeftRight size={16} /></div>
                            <div>
                                <div className="label-eyebrow mb-1.5">Target · B</div>
                                <Select value={b} onValueChange={setB}>
                                    <SelectTrigger data-testid="compare-b-select" className="bg-[var(--bg-surface-2)] border-white/10 text-xs h-9">
                                        <SelectValue placeholder="Choose workspace B" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                                        {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <button
                                data-testid="compare-run"
                                onClick={run}
                                disabled={loading || !a || !b || a === b}
                                className="btn-primary flex items-center gap-2 h-9 disabled:opacity-50"
                            >
                                {loading ? <Loader2 size={12} className="animate-spin" /> : <ArrowLeftRight size={12} />}
                                {loading ? "Comparing…" : "Run Diff"}
                            </button>
                        </div>
                        {workspaces.length < 2 && (
                            <p
                                data-testid="compare-single-workspace-hint"
                                className="mt-3 text-[11px] font-mono text-amber-300/80 uppercase tracking-wider"
                            >
                                Cross-workspace diff needs at least two workspaces — use the Timeline Diff tab to compare
                                this workspace against its own earlier analysis runs.
                            </p>
                        )}
                        {workspaces.length >= 2 && a && a === b && (
                            <p
                                data-testid="compare-same-workspace-hint"
                                className="mt-3 text-[11px] font-mono text-amber-300/80 uppercase tracking-wider"
                            >
                                Baseline and target are the same workspace — pick a different target to run the diff.
                            </p>
                        )}
                    </Panel>

                    <DiffResultView result={result} labelA="A" labelB="B" />
                </TabsContent>

                <TabsContent value="timeline" className="pt-4">
                    <TimelineDiffPanel />
                </TabsContent>
            </Tabs>
        </div>
    );
}
